import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {useQuery} from "@tanstack/react-query";
import associationNetworkService from "../../../../services/web/association-network-service.js";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import bosService from "../../../../services/web/bos-service.js";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import ShowHideColumnsGrid from "../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";

const initialFilter = {
    name: "",
}

const storageItem = "network-hidden-columns"

const AssociationNetworkList = () => {
    const navigate = navigateHook();
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);
    const [filter, setFilter] = useState(initialFilter);

    const [columnDefs, setColumnDefs] = useState([]);

    const {data = [], isLoading} = useQuery({
        queryKey: ['association-network'],
        queryFn: async () => await associationNetworkService.list(),
    });

    const {data: boName = [], isLoadingBoName, status} = useQuery({
        queryKey: ['bo-name'],
        queryFn: async () => await bosService.getBoName(),
    });

    useEffect(() => {
        if (status === "success") {
            const columns = [
                {headerName: "Nome", field: "name", flex:1, filter: "agTextColumnFilter", filterParams: aggridUtils.filterParams, comparator: aggridUtils.nullLastComparator, minWidth: 140},
                {headerName: "D/H criação", field: "createdDate", width: 130, valueFormatter: ({value}) => projectUtils.formatDate(value)},
                {headerName: "D/H última alteração", field: "updatedDate", width: 155, valueFormatter: ({value}) => projectUtils.formatDate(value)},
                {headerName: "Indivíduos", field: "individuals", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Veículos", field: "vehicles", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Abordagens", field: "encounters", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Ocorrências", field: "occurrences", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "SOS", field: "sos", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: boName, field: "bos", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Análises", field: "analysis", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Passagens", field: "passages", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Rede social", field: "socialMedia", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Empresa", field: "company", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Notícia", field: "news", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Imagem", field: "image", width: 100, comparator: aggridUtils.nullLastComparator},
                {headerName: "Anotação", field: "annotation", width: 100, comparator: aggridUtils.nullLastComparator},
            ]

            const hiddenColumns = JSON.parse(localStorage.getItem(storageItem));
            if (hiddenColumns) columns.forEach(c => hiddenColumns.includes(c.field) ? c.hide = true : "");

            setColumnDefs(columns);
        }
    }, [boName, status]);

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field)
    }

    const toggleColumn = (field) => {
        setColumnDefs((prevDefs) =>
            prevDefs.map((col) => col.field === field ? {...col, hide: !col.hide} : col)
        );
    }

    const defaultColDef = useMemo(() => {
        return {
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
            suppressHeaderContextMenu: true,
            suppressFloatingFilterButton: true,
        };
    }, []);

    const onChange = useCallback(({ target: { id, value } }) => {
        setFilter(prev => ({ ...prev, [id]: value }));
    }, []);

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (!value) return;
            filterModel[field] = {
                filterType: "text",
                type: "contains",
                filter: value
            }
        });

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    useGlobalKey("Enter", search);
    useGlobalKey("Escape", clearFilter);

    const onRowClicked = async (event) => {
        const id = event.data.id;
        await associationNetworkService.auditView(id);

        const urlDev = `/association-network/form/${id}`;
        navigate(urlDev)
    }

    const openForm = () => {
        const urlDev = `/association-network/form`;
        navigate(urlDev)
    }

    return (
        <Template isLoading={isLoading || isLoadingBoName}>
            <Template.Header>
                <Template.Title>
                    Rede de associação
                </Template.Title>
                <Button onClick={openForm}>
                    <FaPlus/><span className="font-bold">Adicionar rede de associação</span>
                </Button>
            </Template.Header>
            <Template.Body>

                <Card>
                    <Card.Body>
                        <SentryRow>
                            <div className={"col-span-12 flex gap-4"}>
                                <InputForm id="name" name="name" value={filter.name} onChange={onChange}
                                           label="Nome" classNameWrapper={"w-full"}/>
                                <div className={"flex gap-2"}>
                                    <Button target="primary-outline" onClick={clearFilter}>
                                        <FaEraser/>
                                    </Button>
                                    <Button onClick={search}><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card className="mt-2">
                    <Card.Body>
                        <div className={"aggrid-row-pointer h-[calc(calc(100vh-153px))]"}>
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={data || []}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                onRowClicked={onRowClicked}
                                onColumnVisible={onColumnVisible}
                                pagination
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={storageItem}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default AssociationNetworkList;